import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Client } from "@src/api/ClientApiSlice";
import { SubjectSyllabus } from "@src/api/SyllabusApiSlice";
import { encryptData } from "@src/utils/crypto";

export interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  challengesCount: number;
  questionTypes: string[];
}

interface ParentIdLevel {
  id: string;
  name: string;
}

interface ParentIdState {
  org: ParentIdLevel;
  subOrg: ParentIdLevel;
  client: ParentIdLevel;
  branch: ParentIdLevel;
  batch: ParentIdLevel;
}

interface GlobalState {
  isElectronApp: boolean;
  resourceId: string;
  nodeResourceId: string;
  batchId: string;
  resourceType: string;
  parentTreeState: {
    resourceId?: string;
    resourceType?: string;
  };
  userDetails: any;
  studentDetails: any;
  selectedNode?: any;
  organizationData?: any;
  challengeCategoryData?: {
    id: string;
    name: string;
    description?: string;
    challengesCount?: number;
    questionTypes?: string[];
  };
  studentTournamentData?: any;
  loginStatus?: string;
  selectedTournamentId?: string;
  orgLogo?: string | null;
  isLogin?: boolean;
  selectedChallengeCategory?: { id: string; name: string; icon: string } | null;
  classStarted: boolean;
  batchCode: string;
  externalLink: string;
  challengeCategory?: Category[];
  quizId?: string;
  quizGroupId?: string;
  isRunClicked?: boolean;
  intendedPath?: {
    key: string;
    path: string;
  };
  isError?: boolean;
  selectedOrganizationName: string;
  theme:
    | "website-theme"
    | "website-light-theme"
    | "app-theme"
    | "editor-dark-theme"
    | "editor-light-theme";
  isSubmitQuestionClicked?: boolean;
  navigateWebsite?: boolean;
  tournamentListView: "list" | "grid";
  clientSelectedTab: string;
  clientData?: Client | null;
  dashboardStudentCount?: number;
  isTaskRoom?: boolean | null;
  classRoomAllowed?: boolean | null;
  isChallengeCategoryFetching?: boolean;
  apiTriggerKey?: number | null;
  isGlobalChatbotVisible: boolean;
  isCameraActive: boolean;
  isThreatDetected: boolean;
  detectedThreatType: string;
  detectedThreatLabel: string;
  isTrainerTaskRoom: boolean;
  parentId: ParentIdState;
  downloadToken?: string;
}
const initialState: GlobalState = {
  isElectronApp: false,
  resourceId: "",
  nodeResourceId: "",
  batchId: "",
  resourceType: "",
  parentTreeState: {
    resourceId: "",
    resourceType: "",
  },
  userDetails: null,
  studentDetails: null,
  downloadToken: "",
  selectedNode: undefined,
  organizationData: null,
  challengeCategoryData: {
    id: "",
    name: "",
    description: "",
    challengesCount: 0,
    questionTypes: [""],
  },
  studentTournamentData: null,
  loginStatus: "login",
  isLogin: false,
  selectedChallengeCategory: null,
  classStarted: false,
  batchCode: "",
  externalLink: "",
  challengeCategory: [],
  quizId: "",
  quizGroupId: "",
  isRunClicked: false,
  intendedPath: {
    key: "",
    path: "",
  },
  isError: false,
  selectedOrganizationName: "",
  theme: "website-theme",
  isSubmitQuestionClicked: false,
  navigateWebsite: false,
  tournamentListView: "list",
  isTrainerTaskRoom: true,
  isGlobalChatbotVisible: true,
  isCameraActive: false,
  isThreatDetected: false,
  detectedThreatType: "none",
  detectedThreatLabel: "",
  parentId: {
    org: { id: "", name: "" },
    subOrg: { id: "", name: "" },
    client: { id: "", name: "" },
    branch: { id: "", name: "" },
    batch: { id: "", name: "" },
  },
  clientSelectedTab: "Dashboard",
  clientData: null,
  dashboardStudentCount: 0,
  isTaskRoom: false,
  classRoomAllowed: false,
  isChallengeCategoryFetching: false,
  apiTriggerKey: null,
};
const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setGlobalState: (state, action: PayloadAction<Partial<GlobalState>>) => {
      return { ...state, ...action.payload };
    },
    setUserDetails: (state, action) => {
      state.userDetails = action.payload;
      const encrypted = encryptData(action.payload);
      localStorage.setItem("userDetails", encrypted);
    },
    setStudentDetails: (state, action) => {
      state.studentDetails = action.payload;
    },
    updateNode: (state, action) => {
      state.selectedNode = action.payload;
    },
    setOrganizationData: (state, action) => {
      state.organizationData = action.payload;
    },
    setSelectedOrganizationName: (state, action) => {
      state.selectedOrganizationName = action.payload;
    },
    setChallengeCategoryData: (state, action) => {
      state.challengeCategoryData = action.payload;
    },
    setStudentTournamentData: (state, action) => {
      state.studentTournamentData = action.payload;
    },
    setUserLogin: (state, action) => {
      state.isLogin = action.payload;
    },
    setNavigateWebsite: (state, action) => {
      state.navigateWebsite = action.payload;
    },
    setQuizId: (state, action: PayloadAction<string>) => {
      state.quizId = action.payload;
    },
    setQuizGroupId: (state, action: PayloadAction<string>) => {
      state.quizGroupId = action.payload;
    },
    setOrgLogo: (state, action: PayloadAction<string | null>) => {
      state.orgLogo = action.payload;
    },
    setSelectedChallengeCategory: (
      state,
      action: PayloadAction<{ id: string; name: string; icon: string }>,
    ) => {
      state.selectedChallengeCategory = action.payload;
    },
    clearUserDetails: (state) => {
      state.userDetails = null;
      localStorage.removeItem("userDetails");
    },
    setCategoryDropdownData: (state, action: PayloadAction<Category[]>) => {
      state.challengeCategory = action.payload;
    },
    setIntendedPath: (
      state,
      action: PayloadAction<{ key: string; path: string }>,
    ) => {
      state.intendedPath = action.payload;
    },
    clearIntendedPath: (state) => {
      state.intendedPath = { key: "", path: "" };
    },
    setTheme: (
      state,
      action: PayloadAction<
        | "website-theme"
        | "website-light-theme"
        | "app-theme"
        | "editor-dark-theme"
        | "editor-light-theme"
      >,
    ) => {
      const nextTheme = action.payload;
      if (nextTheme === "website-theme" && state.theme === "website-light-theme") {
        return;
      }
      state.theme = nextTheme;
    },
    toggleWebsiteTheme: (state) => {
      state.theme =
        state.theme === "website-theme" ? "website-light-theme" : "website-theme";
    },
    setParentIdByLevel: (
      state,
      action: PayloadAction<{
        level: "ORG" | "SUB_ORG" | "CLIENT" | "BRANCH" | "BATCH";
        id: string;
        name: string;
      }>,
    ) => {
      const { level, id, name } = action.payload;

      switch (level) {
        case "ORG":
          state.parentId = {
            org: { id, name },
            subOrg: { id: "", name: "" },
            client: { id: "", name: "" },
            branch: { id: "", name: "" },
            batch: { id: "", name: "" },
          };
          break;

        case "SUB_ORG":
          state.parentId.subOrg = { id, name };
          state.parentId.client = { id: "", name: "" };
          state.parentId.branch = { id: "", name: "" };
          state.parentId.batch = { id: "", name: "" };
          break;

        case "CLIENT":
          state.parentId.client = { id, name };
          state.parentId.branch = { id: "", name: "" };
          state.parentId.batch = { id: "", name: "" };
          break;

        case "BRANCH":
          state.parentId.branch = { id, name };
          state.parentId.batch = { id: "", name: "" };
          break;

        case "BATCH":
          state.parentId.batch = { id, name };
          break;
      }
    },
    setClientData: (state, action: PayloadAction<Client | null>) => {
      state.clientData = action.payload;
    },
  },
});
export const {
  setGlobalState,
  setUserDetails,
  setStudentDetails,
  updateNode,
  clearUserDetails,
  setOrganizationData,
  setChallengeCategoryData,
  setUserLogin,
  setOrgLogo,
  setStudentTournamentData,
  setSelectedChallengeCategory,
  setCategoryDropdownData,
  setQuizGroupId,
  setNavigateWebsite,
  setQuizId,
  setIntendedPath,
  clearIntendedPath,
  setTheme,
  toggleWebsiteTheme,
  setSelectedOrganizationName,
  setParentIdByLevel,
  setClientData,
} = globalSlice.actions;
export default globalSlice.reducer;
